import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    Button,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Alert,
    CircularProgress,
    Stack,
    Tooltip,
    Grid,
    Card,
    CardContent,
    MenuItem
} from '@mui/material';
import {
    CheckCircle,
    Cancel,
    Visibility,
    Edit,
    Undo,
    Person,
    Schedule,
    CalendarToday
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { formatDate, formatDateTimeCentral } from '../../utils/constants';
import apiClient from '../../api';
import { isOfficeTimeOffRequest } from '../../utils/personnelType';

const LABEL_END_DISPATCH = 'Date to Be In';
const LABEL_BEGIN_DISPATCH = 'Date to Leave Back Out';

function TimeOffScheduleLines({ request, variant = 'caption' }) {
    if (isOfficeTimeOffRequest(request)) {
        if (request.isPartialDay && request.partialDayStartTime) {
            return (
                <Stack spacing={0.25}>
                    <Typography variant={variant} color="text.secondary">
                        Leave at: {request.partialDayStartTime}
                    </Typography>
                    <Typography variant={variant} color="text.secondary">
                        Back at: {request.partialDayEndTime}
                    </Typography>
                </Stack>
            );
        }
        return (
            <Typography variant={variant} color="text.secondary">All Day</Typography>
        );
    }
    if (request.leavingYardAt || request.backToYardAt) {
        return (
            <Stack spacing={variant === 'body2' ? 0.5 : 0.25}>
                <Typography variant={variant} color="text.secondary">
                    {LABEL_END_DISPATCH}:{' '}
                    {request.leavingYardAt ? formatDateTimeCentral(request.leavingYardAt) : '—'}
                </Typography>
                <Typography variant={variant} color="text.secondary">
                    {LABEL_BEGIN_DISPATCH}:{' '}
                    {request.backToYardAt ? formatDateTimeCentral(request.backToYardAt) : '—'}
                </Typography>
            </Stack>
        );
    }
    return <Typography variant={variant} color="text.secondary">—</Typography>;
}

const TimeOffRequestList = ({ refreshTrigger, prependItem }) => {
    const { user } = useAuth();
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [withdrawReason, setWithdrawReason] = useState('');
    const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
    const [cancelReason, setCancelReason] = useState('');
    const [editOpen, setEditOpen] = useState(false);
    const [reviewNotes, setReviewNotes] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [screenWidth, setScreenWidth] = useState(window.innerWidth);

    const isAdmin = user?.role === 'admin' || user?.role === 'company_admin';
    const isOwner = (request) => request?.submittedBy?._id === user?._id;

    // Log screen size changes
    useEffect(() => {
        const handleResize = () => {
            const newWidth = window.innerWidth;
            setScreenWidth(newWidth);
            console.log('[TimeOffRequestList] Screen resized:', {
                width: newWidth,
                height: window.innerHeight,
                isMobile: newWidth < 900,
                isDesktop: newWidth >= 900,
                breakpoint: newWidth < 600 ? 'xs' : newWidth < 900 ? 'sm' : newWidth < 1200 ? 'md' : newWidth < 1536 ? 'lg' : 'xl'
            });
        };

        console.log('[TimeOffRequestList] Initial screen size:', {
            width: window.innerWidth,
            height: window.innerHeight,
            isMobile: window.innerWidth < 900,
            isDesktop: window.innerWidth >= 900
        });

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Log when component renders
    useEffect(() => {
        console.log('[TimeOffRequestList] Component rendered with:', {
            requestCount: requests.length,
            screenWidth,
            shouldShowMobile: screenWidth < 900,
            shouldShowDesktop: screenWidth >= 900
        });
    }, [requests.length, screenWidth]);

    useEffect(() => {
        fetchTimeOffRequests();
    }, [refreshTrigger]);

    // Prepend newly created item without refetch
    useEffect(() => {
        if (prependItem && prependItem._id) {
            setRequests(prev => {
                const exists = prev.some(r => r._id === prependItem._id);
                if (exists) return prev;
                return [prependItem, ...prev];
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [prependItem?._id]);

    const fetchTimeOffRequests = async () => {
        try {
            setLoading(true);
            const response = await apiClient.get('/forms/time-off');
            setRequests(response.data.data || []);
        } catch (error) {
            console.error('Error fetching time off requests:', error);
            setError('Failed to load time off requests');
        } finally {
            setLoading(false);
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'pending':
                return 'warning';
            case 'approved':
                return 'success';
            case 'denied':
                return 'error';
            case 'withdrawn':
                return 'default';
            default:
                return 'default';
        }
    };

    const getRequestTypeLabel = (type) => {
        const types = {
            'sick_doctor': 'Sick/Doctor',
            'vacation_paid': 'Vacation-Paid',
            'bereavement': 'Bereavement',
            'unpaid': 'Time Off Without Pay',
            'military': 'Military',
            'jury_duty': 'Jury Duty',
            'maternity_paternity': 'Maternity/Paternity',
            'other': 'Other',
            // Legacy
            'vacation': 'Vacation',
            'sick_leave': 'Sick Leave',
            'personal': 'Personal',
            'emergency': 'Emergency'
        };
        return types[type] || type;
    };

    const getRequestTypeColor = (type) => {
        switch (type) {
            case 'vacation_paid':
                return 'success';
            case 'sick_doctor':
                return 'info';
            case 'bereavement':
                return 'default';
            case 'unpaid':
                return 'warning';
            case 'military':
                return 'secondary';
            case 'jury_duty':
                return 'primary';
            case 'maternity_paternity':
                return 'secondary';
            default:
                return 'default';
        }
    };

    const calculateDays = (startDate, endDate) => {
        const start = new Date(startDate);
        const end = new Date(endDate);
        const diffTime = Math.abs(end - start);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
        return diffDays;
    };

    const handleReviewRequest = (request, newStatus) => {
        setSelectedRequest({ ...request, newStatus });
        setReviewNotes('');
        setReviewDialogOpen(true);
    };

    const handleSubmitReview = async () => {
        if (!selectedRequest) return;

        setSubmitting(true);
        setError('');

        try {
            console.log('[TimeOffRequestList] Submitting review', {
                id: selectedRequest._id,
                newStatus: selectedRequest.newStatus,
                hasNotes: Boolean(reviewNotes)
            });
            let res = await apiClient.put(`/forms/time-off/${selectedRequest._id}`, {
                status: selectedRequest.newStatus,
                reviewNotes: reviewNotes
            });
            console.log('[TimeOffRequestList] Review response', res?.status, res?.data);

            // Refresh the list
            await fetchTimeOffRequests();
            
            setReviewDialogOpen(false);
            setSelectedRequest(null);
            setReviewNotes('');
        } catch (error) {
            console.error('[TimeOffRequestList] Error reviewing request (PUT):', error?.message, error?.response?.status, error?.response?.data);
            // Fallback to POST alias if PUT fails with network error
            if (error?.code === 'ERR_NETWORK') {
                try {
                    console.log('[TimeOffRequestList] Retrying with POST /review alias');
                    const res2 = await apiClient.post(`/forms/time-off/${selectedRequest._id}/review`, {
                        status: selectedRequest.newStatus,
                        reviewNotes: reviewNotes
                    });
                    console.warn('[TimeOffRequestList] Review response (POST)', res2?.status, res2?.data);
                    await fetchTimeOffRequests();
                    setReviewDialogOpen(false);
                    setSelectedRequest(null);
                    setReviewNotes('');
                    return;
                } catch (err2) {
                    console.error('[TimeOffRequestList] Fallback POST failed:', err2?.message, err2?.response?.status, err2?.response?.data);
                    setError(err2.response?.data?.message || 'Failed to update request');
                    return;
                }
            } else {
                setError(error.response?.data?.message || 'Failed to update request');
            }
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" p={2}>
                <CircularProgress />
            </Box>
        );
    }

    if (requests.length === 0) {
        return (
            <Paper sx={{ p: 1.5, borderRadius: 2, borderTop: '3px solid', borderTopColor: 'primary.main' }}>
                <Typography variant="h6" gutterBottom>
                    Time Off Requests
                </Typography>
                <Alert severity="info">
                    No time off requests found. Submit your first request above!
                </Alert>
            </Paper>
        );
    }

    return (
        <Box>
            <Paper sx={{ p: 1.5, borderRadius: 2, borderTop: '3px solid', borderTopColor: 'primary.main' }}>
                <Typography variant="h6" gutterBottom>
                    Time Off Requests ({requests.length})
                </Typography>
                
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}
                {successMessage && (
                    <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage('')}>
                        {successMessage}
                    </Alert>
                )}

                {/* Mobile-friendly card view for small screens */}
                <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                    {console.log('[TimeOffRequestList] RENDERING MOBILE CARD VIEW', { count: requests.length, screenWidth })}
                    <Grid container spacing={2}>
                        {requests.map((request) => (
                            <Grid item xs={12} key={request._id}>
                                <Card>
                                    <CardContent>
                                        <Stack spacing={2}>
                                            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                                                <Stack spacing={1}>
                                                    <Typography variant="subtitle1" fontWeight="bold">
                                                        {getRequestTypeLabel(request.requestType)}
                                                    </Typography>
                                                    <Stack direction="row" alignItems="center" spacing={1}>
                                                        <Person fontSize="small" />
                                                        <Typography variant="body2">
                                                            {request.submittedBy?.name}
                                                        </Typography>
                                                    </Stack>
                                                </Stack>
                                                <Chip
                                                    label={request.status}
                                                    color={getStatusColor(request.status)}
                                                    size="small"
                                                />
                                            </Stack>
                                            
                                            <Stack direction="row" alignItems="center" spacing={1}>
                                                <CalendarToday fontSize="small" />
                                                <Typography variant="body2">
                                                    {`${formatDate(request.startDate)} - ${formatDate(request.endDate)}`}
                                                </Typography>
                                            </Stack>
                                            
                                            <Typography variant="body2" color="text.secondary">
                                                Duration: {calculateDays(request.startDate, request.endDate)} days
                                            </Typography>

                                            {request.requestType === 'vacation_paid' && request.paidVacationDays != null && (
                                                <Typography variant="body2" color="text.secondary">
                                                    Paid vacation days to use: {request.paidVacationDays}
                                                </Typography>
                                            )}

                                            <TimeOffScheduleLines request={request} variant="caption" />
                                            
                                            {request.reason && (
                                                <Typography variant="body2">
                                                    <strong>Reason:</strong> {request.reason}
                                                </Typography>
                                            )}
                                            
                                            {(isAdmin || isOwner(request)) && (
                                                <Stack direction="row" spacing={1}>
                                                    {isAdmin && request.status === 'pending' && (
                                                        <>
                                                            <Button
                                                                size="small"
                                                                variant="contained"
                                                                color="success"
                                                                startIcon={<CheckCircle />}
                                                                onClick={() => handleReviewRequest(request, 'approved')}
                                                            >
                                                                Approve
                                                            </Button>
                                                            <Button
                                                                size="small"
                                                                variant="contained"
                                                                color="error"
                                                                startIcon={<Cancel />}
                                                                onClick={() => handleReviewRequest(request, 'denied')}
                                                            >
                                                                Deny
                                                            </Button>
                                                        </>
                                                    )}
                                                    {isOwner(request) && request.status === 'pending' && (
                                                        <>
                                                            <Button size="small" onClick={() => { setSelectedRequest(request); setEditOpen(true);} }>
                                                                Edit
                                                            </Button>
                                                            <Button size="small" color="error" onClick={() => { setSelectedRequest(request); setConfirmOpen(true);} }>
                                                                Withdraw
                                                            </Button>
                                                        </>
                                                    )}
                                                </Stack>
                                            )}
                                        </Stack>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                {/* Desktop table view */}
                <TableContainer sx={{ display: { xs: 'none', md: 'block' }, maxHeight: 560 }}>
                    {console.log('[TimeOffRequestList] RENDERING DESKTOP TABLE VIEW', { count: requests.length, screenWidth })}
                    <Table stickyHeader>
                        <TableHead sx={{ bgcolor: 'rgba(233, 79, 55, 0.08)' }}>
                            <TableRow>
                                <TableCell>Submitted By</TableCell>
                                <TableCell>Type</TableCell>
                                <TableCell>Start Date</TableCell>
                                <TableCell>End Date</TableCell>
                                <TableCell>Yard (leave / return)</TableCell>
                                <TableCell>Duration</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Submitted</TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {requests.map((request) => (
                                <TableRow key={request._id} hover>
                                    <TableCell>
                                        <Stack>
                                            <Typography variant="body2" fontWeight="medium">
                                                {request.submittedBy?.name}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {request.submittedBy?.email}
                                            </Typography>
                                        </Stack>
                                    </TableCell>
                                    <TableCell>
                                        <Chip 
                                            label={getRequestTypeLabel(request.requestType)}
                                            color={getRequestTypeColor(request.requestType)}
                                            variant="outlined"
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>{formatDate(request.startDate)}</TableCell>
                                    <TableCell>{formatDate(request.endDate)}</TableCell>
                                    <TableCell>
                                        <TimeOffScheduleLines request={request} />
                                    </TableCell>
                                    <TableCell>
                                        {request.isPartialDay && request.partialDayHours ? (
                                            <Typography variant="body2">{request.partialDayHours} hour{request.partialDayHours !== 1 ? 's' : ''}</Typography>
                                        ) : (
                                            calculateDays(request.startDate, request.endDate)
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={request.status}
                                            color={getStatusColor(request.status)}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">
                                            {formatDate(request.createdAt)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        {request.status === 'pending' ? (
                                            <Stack direction="row" spacing={1}>
                                                {(isAdmin) && (
                                                    <>
                                                        <Tooltip title="Approve">
                                                            <IconButton
                                                                size="small"
                                                                color="success"
                                                                onClick={() => handleReviewRequest(request, 'approved')}
                                                            >
                                                                <CheckCircle />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Deny">
                                                            <IconButton
                                                                size="small"
                                                                color="error"
                                                                onClick={() => handleReviewRequest(request, 'denied')}
                                                            >
                                                                <Cancel />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </>
                                                )}
                                                {/* Owner can withdraw own pending request */}
                                                {isOwner(request) && (
                                                    <>
                                                        {/* Edit placeholder for future inline edit */}
                                                        <Tooltip title="Edit">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => { setSelectedRequest(request); setEditOpen(true);} }
                                                            >
                                                                <Edit />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Withdraw">
                                                            <IconButton
                                                                size="small"
                                                                color="error"
                                                                onClick={() => { setSelectedRequest(request); setConfirmOpen(true);} }
                                                            >
                                                                <Cancel />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </>
                                                )}
                                            </Stack>
                                        ) : (
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <Stack>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Reviewed by: {request.reviewedBy?.name}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {formatDate(request.reviewedAt)}
                                                    </Typography>
                                                </Stack>
                                                {isOwner(request) && request.status === 'approved' && !request.cancellationRequested && (
                                                    <Button size="small" startIcon={<Undo />} onClick={() => { setSelectedRequest(request); setCancelDialogOpen(true); }}>
                                                        Request Cancellation
                                                    </Button>
                                                )}
                                            </Stack>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            {/* Review Dialog */}
            <Dialog open={reviewDialogOpen} onClose={() => setReviewDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {selectedRequest?.newStatus === 'approved' ? 'Approve' : 'Deny'} Time Off Request
                </DialogTitle>
                <DialogContent>
                    {selectedRequest && (
                        <Box>
                            <Typography variant="body2" paragraph>
                                <strong>Employee:</strong> {selectedRequest.submittedBy?.name}
                            </Typography>
                            <Typography variant="body2" paragraph>
                                <strong>Type:</strong> {getRequestTypeLabel(selectedRequest.requestType)}
                            </Typography>
                            {selectedRequest.requestType === 'vacation_paid' && selectedRequest.paidVacationDays != null && (
                                <Typography variant="body2" paragraph>
                                    <strong>Paid vacation days to use:</strong> {selectedRequest.paidVacationDays}
                                </Typography>
                            )}
                            <Typography variant="body2" paragraph>
                                <strong>{isOfficeTimeOffRequest(selectedRequest) ? 'Dates of Absence' : 'Dates'}:</strong>{' '}
                                {`${formatDate(selectedRequest.startDate)} - ${formatDate(selectedRequest.endDate)}`}
                            </Typography>
                            <Typography variant="body2" paragraph>
                                <strong>Duration:</strong>{' '}
                                {selectedRequest.isPartialDay && selectedRequest.partialDayHours
                                    ? `${selectedRequest.partialDayHours} hour${selectedRequest.partialDayHours !== 1 ? 's' : ''}`
                                    : `${calculateDays(selectedRequest.startDate, selectedRequest.endDate)} days`}
                            </Typography>
                            {isOfficeTimeOffRequest(selectedRequest) && (
                                <Box sx={{ mb: 2 }}>
                                    <TimeOffScheduleLines request={selectedRequest} variant="body2" />
                                </Box>
                            )}
                            {!isOfficeTimeOffRequest(selectedRequest) && (selectedRequest.leavingYardAt || selectedRequest.backToYardAt) && (
                                <Box sx={{ mb: 2 }}>
                                    <TimeOffScheduleLines request={selectedRequest} variant="body2" />
                                </Box>
                            )}
                            {selectedRequest.reason && (
                                <Typography variant="body2" paragraph>
                                    <strong>Reason:</strong> {selectedRequest.reason}
                                </Typography>
                            )}
                            
                            <TextField
                                fullWidth
                                label="Review Notes (Optional)"
                                multiline
                                rows={3}
                                value={reviewNotes}
                                onChange={(e) => setReviewNotes(e.target.value)}
                                placeholder="Add any notes about this decision..."
                                sx={{ mt: 2 }}
                            />
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setReviewDialogOpen(false)} disabled={submitting}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSubmitReview}
                        variant="contained"
                        color={selectedRequest?.newStatus === 'approved' ? 'success' : 'error'}
                        disabled={submitting}
                        startIcon={submitting ? <CircularProgress size={20} /> : null}
                    >
                        {submitting ? 'Processing...' : `${selectedRequest?.newStatus === 'approved' ? 'Approve' : 'Deny'} Request`}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Withdraw Dialog */}
            <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Withdraw Time Off Request</DialogTitle>
                <DialogContent>
                    <Alert severity="info" sx={{ mb: 2 }}>
                        This will mark the request as withdrawn instead of deleting it, preserving audit history.
                    </Alert>
                    <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label="Reason (optional)"
                        value={withdrawReason}
                        onChange={(e) => setWithdrawReason(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={async () => {
                            if (!selectedRequest) return;
                            try {
                                await apiClient.delete(`/forms/time-off/${selectedRequest._id}`);
                                setConfirmOpen(false);
                                setSelectedRequest(null);
                                setWithdrawReason('');
                                fetchTimeOffRequests();
                                setSuccessMessage('Time off request withdrawn.');
                            } catch (e) {
                                console.error('Withdraw failed', e);
                                setError('Failed to withdraw request');
                            }
                        }}
                    >
                        Withdraw
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Request Cancellation Dialog */}
            <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Request Cancellation</DialogTitle>
                <DialogContent>
                    <Alert severity="info" sx={{ mb: 2 }}>
                        This will notify management to review canceling your approved time off.
                    </Alert>
                    <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label="Reason (required)"
                        value={cancelReason}
                        onChange={(e) => setCancelReason(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCancelDialogOpen(false)}>Close</Button>
                    <Button
                        variant="contained"
                        disabled={!cancelReason.trim()}
                        onClick={async () => {
                            if (!selectedRequest) return;
                            try {
                                await apiClient.post(`/forms/time-off/${selectedRequest._id}/request-cancellation`, { reason: cancelReason.trim() });
                                setCancelDialogOpen(false);
                                setSelectedRequest(null);
                                setCancelReason('');
                                fetchTimeOffRequests();
                                setSuccessMessage('Cancellation request submitted for review.');
                            } catch (e) {
                                console.error('Cancellation request failed', e);
                                setError('Failed to request cancellation');
                            }
                        }}
                    >
                        Submit
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Edit Dialog */}
            <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Edit Time Off Request</DialogTitle>
                <DialogContent>
                    {selectedRequest && (
                        <Box sx={{ mt: 1 }}>
                            <TextField
                                fullWidth
                                select
                                size="small"
                                label="Request Type"
                                value={selectedRequest.requestType}
                                onChange={(e) => setSelectedRequest({ ...selectedRequest, requestType: e.target.value })}
                                sx={{ mb: 2 }}
                            >
                                {[ 
                                  { value: 'sick_doctor', label: 'Sick/Doctor' },
                                  { value: 'vacation_paid', label: 'Vacation-Paid' },
                                  { value: 'bereavement', label: 'Bereavement' },
                                  { value: 'unpaid', label: 'Time Off Without Pay' },
                                  { value: 'military', label: 'Military' },
                                  { value: 'jury_duty', label: 'Jury Duty' },
                                  { value: 'maternity_paternity', label: 'Maternity/Paternity' },
                                  { value: 'other', label: 'Other' }
                                ].map(opt => (
                                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                ))}
                            </TextField>
                            <TextField
                                fullWidth
                                multiline
                                rows={3}
                                label="Reason (Optional)"
                                value={selectedRequest.reason || ''}
                                onChange={(e) => setSelectedRequest({ ...selectedRequest, reason: e.target.value })}
                            />
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditOpen(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={async () => {
                            if (!selectedRequest) return;
                            try {
                                await apiClient.patch(`/forms/time-off/${selectedRequest._id}/edit`, {
                                    requestType: selectedRequest.requestType,
                                    reason: selectedRequest.reason || ''
                                });
                                setEditOpen(false);
                                setSelectedRequest(null);
                                fetchTimeOffRequests();
                            } catch (e) {
                                console.error('Edit failed', e);
                                setError('Failed to update request');
                            }
                        }}
                    >
                        Save Changes
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default TimeOffRequestList; 